import { S3Client, PutObjectCommand } from '@aws-sdk/client-s3';
import { v4 as uuidv4 } from 'uuid';

// Get environment variables
const BUCKET_NAME = import.meta.env.VITE_CLOUDFLARE_BUCKET_NAME;
const ACCOUNT_ID = import.meta.env.VITE_CLOUDFLARE_ACCOUNT_ID;
const ACCESS_KEY_ID = import.meta.env.VITE_CLOUDFLARE_ACCESS_KEY_ID;
const SECRET_ACCESS_KEY = import.meta.env.VITE_CLOUDFLARE_SECRET_ACCESS_KEY;
const PUBLIC_DOMAIN = import.meta.env.VITE_CLOUDFLARE_PUBLIC_DOMAIN;

// Validate configuration
const validateConfig = () => {
  const missing = [];
  if (!BUCKET_NAME) missing.push('VITE_CLOUDFLARE_BUCKET_NAME');
  if (!ACCOUNT_ID) missing.push('VITE_CLOUDFLARE_ACCOUNT_ID');
  if (!ACCESS_KEY_ID) missing.push('VITE_CLOUDFLARE_ACCESS_KEY_ID');
  if (!SECRET_ACCESS_KEY) missing.push('VITE_CLOUDFLARE_SECRET_ACCESS_KEY');
  if (!PUBLIC_DOMAIN) missing.push('VITE_CLOUDFLARE_PUBLIC_DOMAIN');

  if (missing.length > 0) {
    throw new Error(`Cloudflare R2 is not properly configured. Missing environment variables: ${missing.join(', ')}`);
  }

  try {
    new URL(PUBLIC_DOMAIN);
  } catch {
    throw new Error('VITE_CLOUDFLARE_PUBLIC_DOMAIN must be a valid URL');
  }
};

// Initialize S3 client
const getS3Client = () => {
  validateConfig();
  
  return new S3Client({
    region: 'auto',
    endpoint: `https://${ACCOUNT_ID}.r2.cloudflarestorage.com`,
    credentials: {
      accessKeyId: ACCESS_KEY_ID,
      secretAccessKey: SECRET_ACCESS_KEY,
    },
    forcePathStyle: true
  });
};

// Valid media types
const VALID_MEDIA_TYPES = {
  image: ['image/jpeg', 'image/png', 'image/gif'],
  video: ['video/mp4', 'video/webm', 'video/quicktime'],
};

// Helper to construct public URL
const getPublicUrl = (fileName: string): string => {
  // Ensure the domain is properly formatted with https:// and no trailing slashes
  const baseUrl = PUBLIC_DOMAIN.replace(/^(https?:\/\/)?/, 'https://')
                              .replace(/\/+$/, '');
  
  // Ensure the filename has no leading slashes
  const cleanFileName = fileName.replace(/^\/+/, '');
  
  return `${baseUrl}/${cleanFileName}`;
};

export async function uploadMedia(file: File): Promise<string> {
  try {
    // Validate file type
    const isValidImage = VALID_MEDIA_TYPES.image.includes(file.type);
    const isValidVideo = VALID_MEDIA_TYPES.video.includes(file.type);

    if (!isValidImage && !isValidVideo) {
      throw new Error('Invalid file type. Only images (JPEG, PNG, GIF) and videos (MP4, WebM, QuickTime) are allowed.');
    }

    // Validate file size (10MB for images, 100MB for videos)
    const maxSize = isValidVideo ? 100 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      throw new Error(`File size must be less than ${isValidVideo ? '100MB' : '10MB'}`);
    }

    // Get S3 client
    const s3Client = getS3Client();

    // Generate unique filename with original extension
    const extension = file.name.split('.').pop()?.toLowerCase() || '';
    const fileName = `${uuidv4()}.${extension}`;

    // Convert File to ArrayBuffer
    const arrayBuffer = await file.arrayBuffer();

    // Create upload command
    const command = new PutObjectCommand({
      Bucket: BUCKET_NAME,
      Key: fileName,
      Body: new Uint8Array(arrayBuffer),
      ContentType: file.type,
      CacheControl: 'max-age=31536000', // 1 year
      ACL: 'public-read',
      Metadata: {
        'original-name': file.name
      }
    });

    // Upload with improved retry logic
    let attempts = 0;
    const maxAttempts = 3;
    let lastError: any;

    while (attempts < maxAttempts) {
      try {
        console.log(`Upload attempt ${attempts + 1}...`);
        
        // Add timeout to the upload request
        const result = await Promise.race([
          s3Client.send(command),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Upload timeout')), 30000)
          )
        ]);

        // Verify successful upload
        if (!result || result.$metadata.httpStatusCode !== 200) {
          throw new Error(`Upload failed with status code: ${result?.$metadata.httpStatusCode || 'unknown'}`);
        }

        // Return the public URL
        return getPublicUrl(fileName);
      } catch (error: any) {
        lastError = error;
        attempts++;

        // Log detailed error information
        console.error(`Upload attempt ${attempts} failed:`, {
          error: error.message,
          code: error.code,
          requestId: error.$metadata?.requestId,
          httpStatusCode: error.$metadata?.httpStatusCode
        });

        // Retry with exponential backoff if not last attempt
        if (attempts < maxAttempts) {
          const delay = Math.min(1000 * Math.pow(2, attempts), 5000);
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }
      }
    }

    throw new Error(`Upload failed after ${attempts} attempts: ${lastError?.message || 'Unknown error'}`);
  } catch (error: any) {
    // Log detailed error information
    console.error('Error uploading media:', {
      error: error.message,
      name: error.name,
      code: error.code,
      metadata: error.$metadata
    });
    throw error;
  }
}